import React from "react";
import {
  ChevronLeft,
  Workflow,
  Hash,
  FileText,
  Layers,
  Paperclip,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { WORKFLOW_KEY_OPTIONS } from "./automationConstants";

const emptyKvPair = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  key: "",
  value: "",
});

export const emptyAutomationKvPair = emptyKvPair;

const formatKvValue = (value) => {
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value == null) return "";
  return String(value);
};

const objectToKvPairs = (obj) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return [emptyKvPair()];
  }
  const pairs = Object.entries(obj).map(([key, value]) => ({
    ...emptyKvPair(),
    key,
    value: formatKvValue(value),
  }));
  return pairs.length > 0 ? pairs : [emptyKvPair()];
};

export const automationObjectToKvPairs = objectToKvPairs;

const SectionCard = ({ icon: Icon, title, description, children }) => (
  <section className="automation-section">
    <div className="automation-section-header">
      <div className="automation-section-title-wrap">
        <span className="automation-section-icon">
          <Icon size={18} />
        </span>
        <div>
          <h2 className="automation-section-title">{title}</h2>
          {description && (
            <p className="automation-section-desc">{description}</p>
          )}
        </div>
      </div>
    </div>
    <div className="automation-section-body">{children}</div>
  </section>
);

export const KeyValueEditor = ({ pairs, onChange, emptyLabel = "No entries yet" }) => {
  const updatePair = (index, field, value) => {
    const next = [...pairs];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const addPair = () => onChange([...pairs, emptyKvPair()]);

  const removePair = (index) => {
    if (pairs.length === 1) {
      onChange([emptyKvPair()]);
      return;
    }
    onChange(pairs.filter((_, i) => i !== index));
  };

  const hasContent = pairs.some((pair) => pair.key?.trim() || pair.value?.trim());

  return (
    <div className="automation-kv-editor">
      <div className="automation-kv-head">
        <span>Key</span>
        <span>Value</span>
        <span />
      </div>
      {pairs.map((pair, index) => (
        <div key={pair.id} className="automation-kv-row">
          <input
            type="text"
            placeholder="e.g. parent_agent_id"
            value={pair.key}
            onChange={(e) => updatePair(index, "key", e.target.value)}
          />
          <input
            type="text"
            placeholder="e.g. 563"
            value={pair.value}
            onChange={(e) => updatePair(index, "value", e.target.value)}
          />
          <button
            type="button"
            className="automation-kv-remove"
            onClick={() => removePair(index)}
            title="Remove row"
            aria-label="Remove row"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ))}
      {!hasContent && (
        <p className="automation-kv-empty">{emptyLabel}</p>
      )}
      <button type="button" className="automation-btn-add" onClick={addPair}>
        <Plus size={13} />
        Add
      </button>
    </div>
  );
};

const FormField = ({ label, required, hint, children, htmlFor, fullWidth = false }) => (
  <div className={`automation-field-item ${fullWidth ? "automation-field-full" : ""}`}>
    <label htmlFor={htmlFor}>
      {label}
      {required && <span className="automation-required">*</span>}
    </label>
    {children}
    {hint && <div className="automation-field-hint-italic">{hint}</div>}
  </div>
);

const emptyFileEntry = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  recordId: null,
  name: "",
  file: null,
  existingFileUrl: "",
  existingContentType: "",
  fileInputKey: 0,
  _destroy: false,
});

const mapApiFileToEntry = (file) => ({
  id: `${file.id ?? Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  recordId: file.id ?? null,
  name: file.name ?? "",
  file: null,
  existingFileUrl: file.file_url ?? "",
  existingContentType: file.content_type ?? "",
  _destroy: false,
});

export const defaultAutomationForm = () => ({
  workflowName: "",
  workflowKey: "",
  agentDuration: "",
  schedulerTime: "",
  agentIdsKv: [emptyKvPair()],
  instructionsKv: [emptyKvPair()],
  contextKv: [emptyKvPair()],
  dataFiles: [],
});

export const mapAutomationToForm = (automation) => {
  const base = defaultAutomationForm();
  if (!automation) return base;

  const agentIds = automation.agent_ids ?? automation.agentIds ?? {};
  const instructions = automation.instructions ?? {};
  const context = automation.context ?? {};
  const filesSource =
    automation.automation_data_files ??
    automation.data_files ??
    automation.extra_files ??
    automation.files ??
    [];

  return {
    workflowName: automation.workflow_name ?? automation.name ?? "",
    workflowKey: automation.workflow_key ?? "",
    agentDuration:
      automation.agent_duration != null ? String(automation.agent_duration) : "",
    schedulerTime:
      automation.scheduler_time != null ? String(automation.scheduler_time) : "",
    agentIdsKv: objectToKvPairs(agentIds),
    instructionsKv: objectToKvPairs(instructions),
    contextKv: objectToKvPairs(
      typeof automation.context_json === "string" && automation.context_json.trim()
        ? (() => {
            try {
              return JSON.parse(automation.context_json);
            } catch {
              return context;
            }
          })()
        : context
    ),
    dataFiles:
      Array.isArray(filesSource) && filesSource.length > 0
        ? filesSource.map(mapApiFileToEntry)
        : [],
  };
};

const AutomationForm = ({
  form,
  setForm,
  isEdit,
  saving,
  onSubmit,
  onCancel,
}) => {
  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateFileEntry = (index, field, value) => {
    setForm((prev) => {
      const dataFiles = [...prev.dataFiles];
      dataFiles[index] = { ...dataFiles[index], [field]: value };
      return { ...prev, dataFiles };
    });
  };

  const addFileEntry = () => {
    setForm((prev) => ({
      ...prev,
      dataFiles: [...prev.dataFiles, emptyFileEntry()],
    }));
  };

  const removeFileEntry = (index) => {
    setForm((prev) => {
      const entry = prev.dataFiles[index];
      if (!entry || entry._destroy) return prev;

      const visibleCount = prev.dataFiles.filter((item) => !item._destroy).length;
      const isLastVisible = visibleCount === 1;
      const keepDestroyed = () => prev.dataFiles.filter((item) => item._destroy);

      if (entry.recordId) {
        if (entry.file) {
          const dataFiles = [...prev.dataFiles];
          dataFiles[index] = {
            ...entry,
            file: null,
            fileInputKey: (entry.fileInputKey ?? 0) + 1,
          };
          return { ...prev, dataFiles };
        }

        const dataFiles = [...prev.dataFiles];
        dataFiles[index] = {
          ...entry,
          _destroy: true,
          file: null,
          fileInputKey: (entry.fileInputKey ?? 0) + 1,
        };
        return { ...prev, dataFiles };
      }

      if (isLastVisible) {
        return { ...prev, dataFiles: keepDestroyed() };
      }

      if (entry.file) {
        const dataFiles = [...prev.dataFiles];
        dataFiles[index] = {
          ...entry,
          file: null,
          fileInputKey: (entry.fileInputKey ?? 0) + 1,
        };
        return { ...prev, dataFiles };
      }

      return {
        ...prev,
        dataFiles: prev.dataFiles.filter((_, i) => i !== index),
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.workflowName?.trim()) {
      toast.error("Workflow name is required");
      return;
    }
    if (!form.workflowKey?.trim()) {
      toast.error("Workflow key is required");
      return;
    }
    onSubmit(form);
  };

  const visibleFileCount = form.dataFiles.filter((f) => !f._destroy).length;

  return (
    <div className="automation-page">
      <button type="button" className="automation-back-btn" onClick={onCancel}>
        <ChevronLeft size={18} />
        Back to automations
      </button>

      <div className="automation-form-hero">
        <h1 className="automation-page-title">
          {isEdit ? "Edit Automation" : "Create Automation"}
        </h1>
        <p className="automation-form-subtitle">
          {isEdit
            ? "Update workflow settings, agents, and data files."
            : "Set up a scheduled workflow with agents, instructions, and optional data files."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="automation-form">
        <SectionCard
          icon={Workflow}
          title="Workflow settings"
          description="Name your automation and choose how often it runs."
        >
          <div className="automation-fields-2col">
            <FormField label="Workflow name" required htmlFor="workflow-name">
              <input
                id="workflow-name"
                type="text"
                value={form.workflowName}
                onChange={(e) => updateField("workflowName", e.target.value)}
                required
              />
            </FormField>

            <FormField
              label="Workflow key"
              required
              htmlFor="workflow-key"
            >
              <select
                id="workflow-key"
                value={form.workflowKey}
                onChange={(e) => updateField("workflowKey", e.target.value)}
                required
              >
                <option value="">Select workflow type</option>
                {WORKFLOW_KEY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Agent duration" htmlFor="agent-duration">
              <input
                id="agent-duration"
                type="text"
                value={form.agentDuration}
                onChange={(e) => updateField("agentDuration", e.target.value)}
              />
            </FormField>

            <FormField
              label="Scheduler time"
              htmlFor="scheduler-time"
              hint="Repeat interval in hours (e.g. 6 = every 6 hours). Set 0 or leave blank to disable automatic runs. Cron checks every 1 minute while testing."
            >
              <input
                id="scheduler-time"
                type="text"
                value={form.schedulerTime}
                onChange={(e) => updateField("schedulerTime", e.target.value)}
              />
            </FormField>
          </div>
        </SectionCard>

        <SectionCard
          icon={Hash}
          title="Agent IDs"
          description="Link agents used in this workflow."
        >
          <KeyValueEditor
            pairs={form.agentIdsKv}
            onChange={(pairs) => updateField("agentIdsKv", pairs)}
            emptyLabel="Add agent IDs required by your workflow type."
          />
        </SectionCard>

        <SectionCard
          icon={FileText}
          title="Instructions"
          description="Queries, keys, and rules passed to agents."
        >
          <KeyValueEditor
            pairs={form.instructionsKv}
            onChange={(pairs) => updateField("instructionsKv", pairs)}
            emptyLabel="Add instruction fields for your agents."
          />
        </SectionCard>

        <SectionCard
          icon={Layers}
          title="Context"
          description="Extra context such as timezone or environment values."
        >
          <KeyValueEditor
            pairs={form.contextKv}
            onChange={(pairs) => updateField("contextKv", pairs)}
            emptyLabel="Optional context fields for the workflow."
          />
        </SectionCard>

        <SectionCard
          icon={Paperclip}
          title="Data files"
          description="Attach spreadsheets or documents used during runs."
        >
            {form.dataFiles.map((entry, fileIndex) => {
              if (entry._destroy) return null;

              const displayIndex =
                form.dataFiles
                  .slice(0, fileIndex + 1)
                  .filter((item) => !item._destroy).length;

              return (
                <div key={entry.id} className="automation-file-block">
                  <div className="automation-file-block-header">
                    <span className="automation-file-label">
                      <Paperclip size={14} />
                      File {displayIndex}
                    </span>
                    <button
                      type="button"
                      className="automation-btn-ghost automation-btn-remove"
                      onClick={() => removeFileEntry(fileIndex)}
                    >
                      <X size={14} />
                      {entry.recordId && entry.file ? "Clear file" : "Remove"}
                    </button>
                  </div>
                  <div className="automation-fields-2col">
                    <div className="automation-field-item">
                      <label>Display name</label>
                      <input
                        type="text"
                        placeholder="e.g. HSN Mapping"
                        value={entry.name}
                        onChange={(e) => updateFileEntry(fileIndex, "name", e.target.value)}
                      />
                    </div>
                    <div className="automation-field-item">
                      <label>{entry.recordId ? "Replace file" : "Upload file"}</label>
                      <label className="automation-file-upload">
                        <input
                          key={`${entry.id}-${entry.fileInputKey ?? 0}`}
                          type="file"
                          className="automation-file-input"
                          onChange={(e) => {
                            const file = e.target.files?.[0] ?? null;
                            updateFileEntry(fileIndex, "file", file);
                          }}
                        />
                        <span className="automation-file-upload-text">
                          {entry.file
                            ? entry.file.name
                            : "Choose a file or drag here"}
                        </span>
                      </label>
                      {entry.recordId && !entry.file && (
                        <div className="automation-existing-file">
                          <span className="automation-field-hint">Current file</span>
                          {entry.existingFileUrl ? (
                            <a
                              href={entry.existingFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="automation-file-link"
                            >
                              {entry.name || "View file"}
                              {entry.existingContentType
                                ? ` · ${entry.existingContentType}`
                                : ""}
                            </a>
                          ) : (
                            <span className="automation-field-hint">
                              {entry.name || "Uploaded file"}
                              {entry.existingContentType
                                ? ` · ${entry.existingContentType}`
                                : ""}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {visibleFileCount === 0 && (
              <div className="automation-files-empty">
                <Paperclip size={20} />
                <span>No data files attached</span>
              </div>
            )}
            <button type="button" className="automation-btn-add" onClick={addFileEntry}>
              <Plus size={13} />
              Add file
            </button>
        </SectionCard>

        <div className="automation-actions">
          <button
            type="button"
            className="automation-btn-secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="automation-btn-primary" disabled={saving}>
            {saving ? "Saving..." : isEdit ? "Save changes" : "Create automation"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AutomationForm;
